import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { api } from '@/lib/api';
import { useApp } from '@/context/AppContext';
import { setLocale, getLocale } from '@/i18n';

/**
 * Iter 31 / R3 fix — The previous implementation forced `user.locale` onto the
 * client on every mount, which silently reverted ES selections back to EN on
 * route changes. We now:
 *   1) Read the current locale from i18n (which is the source of truth and is
 *      already initialised from localStorage at app start).
 *   2) Only sync user.locale → i18n exactly ONCE at first authentication
 *      (when user transitions from null to an object), and only if the user
 *      hasn't yet made an explicit choice (no localStorage value).
 *   3) Trigger a controlled re-render via i18n's onLanguageChanged event so
 *      the toggle's visual state stays in sync after switches.
 */
const LANGS = [
  { code: 'es', short: 'ES' },
  { code: 'en', short: 'EN' },
];

const STORAGE_KEY = 'cth_locale';

export default function LanguageSwitcher({ compact = false }) {
  const { i18n } = useTranslation();
  const { user, setUser } = useApp();
  const [current, setCurrent] = useState(() => getLocale());

  // Subscribe to i18n language changes so our visual state stays accurate
  // (e.g. when setLocale fires from elsewhere).
  useEffect(() => {
    const handler = (lng) => setCurrent(lng?.startsWith('en') ? 'en' : 'es');
    i18n.on('languageChanged', handler);
    return () => i18n.off('languageChanged', handler);
  }, [i18n]);

  // On first authentication, if the user has never made a manual choice
  // (no cth_locale in localStorage), seed from the user's saved preference.
  // We deliberately do NOT re-apply user.locale on every user update — that
  // was the bug that prevented manual switches from sticking.
  useEffect(() => {
    if (!user?.id) return;
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored && user.locale) {
        setLocale(user.locale);
      }
    } catch { /* localStorage disabled — no-op */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const change = async (code) => {
    if (code === current) return;
    setLocale(code);
    setCurrent(code);
    if (user) {
      try {
        const { data } = await api.patch('/auth/me/preferences', { locale: code });
        if (setUser) setUser(data);
      } catch {
        /* silent — local change still applies */
      }
    }
  };

  return (
    <div
      className="inline-flex items-center gap-0.5 rounded-md border border-border bg-background/90 backdrop-blur-sm p-0.5 shadow-sm"
      data-testid="language-switcher"
      role="group"
      aria-label="Language"
    >
      {!compact && <Globe className="h-4 w-4 text-muted-foreground ml-1.5" aria-hidden />}
      {LANGS.map((l) => {
        const active = current === l.code;
        return (
          <button
            key={l.code}
            type="button"
            onClick={() => change(l.code)}
            className={[
              'h-7 px-2.5 rounded-[5px] text-xs font-semibold tracking-wide transition-colors',
              active
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-transparent text-foreground/70 hover:bg-muted hover:text-foreground',
            ].join(' ')}
            data-testid={`lang-${l.code}`}
            aria-pressed={active}
          >
            {l.short}
          </button>
        );
      })}
    </div>
  );
}

export { LanguageSwitcher };
